import { motion } from 'framer-motion'
import { Calendar, Clock, Mail, Phone, Send, CheckCircle, AlertCircle, Video, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import emailjs from '@emailjs/browser'
import SEO from '../components/SEO'
import { db } from '../firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const Consultation = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    consultationType: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Save to Firebase
      await addDoc(collection(db, 'consultationRequests'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        consultationType: formData.consultationType,
        message: formData.message,
        createdAt: serverTimestamp(),
        status: 'pending'
      })
      
      // Send email
      const result = await emailjs.send(
        'service_tn8xlnj',
        'template_anw5r65',
        {
          name: formData.name,
          user_email: formData.email,
          message: `
🗓️ KONSULTIM FALAS - KËRKESË E RE

📱 Phone: ${formData.phone}
📅 Preferred Date: ${formData.preferredDate}
⏰ Preferred Time: ${formData.preferredTime}
💬 Consultation Type: ${formData.consultationType}

📝 Message:
${formData.message}
          `
        },
        'Ac73yOgPaJV0tu4Ri'
      )
      
      console.log('EmailJS Success:', result)
      setSubmitStatus('success')
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        consultationType: '',
        message: ''
      })
    } catch (error) {
      console.error('EmailJS Error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
      setTimeout(() => setSubmitStatus(null), 5000)
    }
  }

  const consultationTypes = [
    'Video Call (Google Meet/Zoom)',
    'Phone Call',
    'WhatsApp Chat',
    'Email Correspondence'
  ]

  return (
    <>
      <SEO 
        title="Konsultim Falas - Contenta Marketing"
        description="Rezervo një konsultim falas 30-minutësh me ekspertët tanë të marketingut dixhital. Diskuto idetë dhe objektivat e tua."
        keywords="konsultim falas, digital marketing consultation, marketing strategy, contenta marketing"
      />
      
      <div className="mt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-bronze-500/5 rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="inline-block mb-4"
              >
                <div className="px-6 py-2 bg-green-500/10 border border-green-400/30 rounded-full backdrop-blur-sm">
                  <span className="text-green-400 font-semibold">✓ 100% Falas · 30 Minuta</span>
                </div>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
                Rezervo një <span className="text-gradient-gold">Konsultim Falas</span>
              </h1>
              <p className="text-xl text-beige-200 max-w-3xl mx-auto leading-relaxed">
                Bisedë 30-minutëshe me ekspertët tanë. Diskutojmë idetë, strategjitë dhe zgjidhjet më të mira për biznesin tënd.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Consultation Form & Benefits */}
        <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="card-premium">
                  <h2 className="text-3xl font-display font-bold text-cream mb-2">
                    Book Your Session
                  </h2>
                  <p className="text-beige-200 mb-8">
                    Zgjedh kohën që të përshtatet më mirë dhe ne do të të konfirmojmë brenda disa orëve.
                  </p>

                  {/* Success Message */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg flex items-center gap-3"
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <div>
                        <p className="text-green-400 font-semibold">Rezervimi u krye me sukses!</p>
                        <p className="text-green-300 text-sm">Do të të kontaktojmë brenda disa orëve për konfirmim.</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Error Message */}
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg flex items-center gap-3"
                    >
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <p className="text-red-400">Ndodhi një gabim. Provoni përsëri ose na kontaktoni direkt.</p>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-cream font-semibold mb-2">Emri i Plotë *</label>
                      <input
                        type="text"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Emri Mbiemri"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-cream font-semibold mb-2">Email *</label>
                        <input
                          type="email"
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="email@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-cream font-semibold mb-2">Telefon *</label>
                        <input
                          type="tel"
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+355 69 XXX XXXX"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                          <Calendar className="w-4 h-4 text-gold-400" />
                          Data Preferuar *
                        </label>
                        <input
                          type="date"
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                          value={formData.preferredDate}
                          onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                          <Clock className="w-4 h-4 text-gold-400" />
                          Ora Preferuar *
                        </label>
                        <input
                          type="time"
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                          value={formData.preferredTime}
                          onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-cream font-semibold mb-2">Lloji i Konsultimit *</label>
                      <select
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                        value={formData.consultationType}
                        onChange={(e) => setFormData({ ...formData, consultationType: e.target.value })}
                      >
                        <option value="">Zgjidh llojin</option>
                        {consultationTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-cream font-semibold mb-2">Mesazhi (Opsionale)</label>
                      <textarea
                        rows={4}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors resize-none disabled:opacity-50"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Na trego pak për çfarë do të diskutojmë..."
                      />
                    </div>

                    <button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>{isSubmitting ? 'Duke rezervuar...' : 'Rezervo Konsultimin'}</span>
                      <Send className={`w-5 h-5 ${isSubmitting ? 'animate-pulse' : ''}`} />
                    </button>
                  </form>
                </div>
              </motion.div>

              {/* Benefits & Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-3xl font-display font-bold text-cream mb-6">
                    Çfarë Përfshin?
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-bronze-900/30 backdrop-blur-sm rounded-xl border border-gold-400/20">
                    <div className="w-10 h-10 bg-gradient-gold rounded-lg flex items-center justify-center flex-shrink-0">
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-cream mb-1">30 Minuta Konsultim</h3>
                      <p className="text-beige-200 text-sm">Video call ose phone call me ekspertët tanë të marketingut</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-bronze-900/30 backdrop-blur-sm rounded-xl border border-gold-400/20">
                    <div className="w-10 h-10 bg-gradient-bronze rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-cream mb-1">Strategji Personale</h3>
                      <p className="text-beige-200 text-sm">Këshilla të personalizuara për biznesin dhe objektivat tua</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-bronze-900/30 backdrop-blur-sm rounded-xl border border-gold-400/20">
                    <div className="w-10 h-10 bg-gradient-gold rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-cream mb-1">Action Plan</h3>
                      <p className="text-beige-200 text-sm">Hapat konkretë që mund të ndiqësh për të arritur rezultate</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-bronze-900/30 backdrop-blur-sm rounded-xl border border-gold-400/20">
                    <div className="w-10 h-10 bg-gradient-bronze rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-cream mb-1">Follow-up Email</h3>
                      <p className="text-beige-200 text-sm">Përmbledhje dhe rekomandime të shkruara pas konsultimit</p>
                    </div>
                  </div>
                </div>

                <div className="card-premium bg-green-500/5 border-green-400/30">
                  <h3 className="text-xl font-bold text-green-400 mb-3">🎁 Bonus për Konsultim</h3>
                  <ul className="space-y-2 text-beige-200">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>Free website audit (nëse aplikueshme)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>10% zbritje nëse fillon projekt brenda 7 ditëve</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>Akses në resources dhe templates falas</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6 bg-bronze-900/30 backdrop-blur-sm rounded-2xl border border-gold-400/20">
                  <p className="text-beige-200 text-center italic">
                    "Konsultimi më ndihmoi të kuptoj saktësisht çfarë më duhej për të rritur biznesin tim online."
                  </p>
                  <p className="text-gold-400 text-center mt-3 font-semibold">- Klient i kënaqur</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Consultation
